import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fragment, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { InsightBar } from '../../components/charts/InsightBar.jsx';
import { useChartPalette } from '../../components/charts/useChartPalette.js';
import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.jsx';
import { EmptyState } from '../../components/ui/EmptyState.jsx';
import { Skeleton } from '../../components/ui/Skeleton.jsx';
import { showToast } from '../../components/ui/Toaster.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { financeApi } from '../../services/financeApi.js';
import { salesApi } from '../../services/salesApi.js';
import { addDaysIso, formatCurrency, formatDate, todayIso } from '../../utils/formatters.js';
import { countBy, sumBy } from '../../utils/insightStats.js';

import { RecurringInvoiceModal } from './RecurringInvoiceModal.jsx';

const INTERVAL_ORDER = ['MONTHLY', 'QUARTERLY', 'YEARLY'];

const INTERVAL_BADGE_VARIANTS = {
  MONTHLY: 'info',
  QUARTERLY: 'warning',
  YEARLY: 'neutral',
};

/** gross → contribution to the monthly volume metric. */
const MONTHLY_FACTOR = { MONTHLY: 1, QUARTERLY: 1 / 3, YEARLY: 1 / 12 };

const CUSTOMER_LOOKUP_LIMIT = 200;
const ID_SHORT_LENGTH = 8;
const DUE_SOON_DAYS = 30;

/** Expanded detail row: lazy-loads the template (runs) on first expand. */
function RecurringRunsRow({ row, colSpan, id }) {
  const { t } = useTranslation();
  const { data, isLoading, error } = useQuery({
    queryKey: ['finance', 'recurring', row.id],
    queryFn: () => financeApi.getRecurring(row.id).then((r) => r.items),
  });
  const runs = data?.runs || [];
  return (
    <tr className="audit-detail-row" id={id}>
      <td colSpan={colSpan}>
        <div className="audit-detail">
          <span className="field-label">{t('recurring.runs.title')}</span>
          {error && <div className="alert danger">{error.message || t('common.loadError')}</div>}
          {isLoading && <Skeleton variant="table" />}
          {!isLoading && !error && runs.length === 0 && (
            <span className="muted text-sm">{t('recurring.runs.empty')}</span>
          )}
          {runs.map((run) => (
            <div
              key={`${run.runDate}-${run.invoiceId || 'deleted'}`}
              style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center' }}
            >
              <span className="mono text-sm">{formatDate(run.runDate)}</span>
              {/* invoiceId is null when the generated draft was later deleted (run stays consumed). */}
              {run.invoiceId ? (
                <Link to={`/finance/invoices/${run.invoiceId}`}>{run.invoiceNumber}</Link>
              ) : (
                <span className="muted text-sm">-</span>
              )}
              {run.invoiceStatus && <Badge status={run.invoiceStatus} />}
              {run.invoiceTotal != null && (
                <span className="mono text-sm">{formatCurrency(run.invoiceTotal)}</span>
              )}
            </div>
          ))}
        </div>
      </td>
    </tr>
  );
}

export function RecurringInvoicesPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const palette = useChartPalette();
  const [expandedId, setExpandedId] = useState(null);
  const [modalState, setModalState] = useState(null); // null | { template, customerLabel }
  const [deleteTarget, setDeleteTarget] = useState(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['finance', 'recurring', 'list'],
    queryFn: () => financeApi.listRecurring().then((r) => r.items || []),
  });

  const customers = useQuery({
    queryKey: ['sales', 'customers', 'lookup'],
    queryFn: () => salesApi.listCustomers({ limit: CUSTOMER_LOOKUP_LIMIT }).then((r) => r.items || []),
  });
  const customerNames = useMemo(
    () => Object.fromEntries((customers.data || []).map((c) => [c.id, c.name])),
    [customers.data],
  );

  const today = todayIso();
  const stats = useMemo(() => {
    const rows = data || [];
    const counts = countBy(rows, (r) => r.interval);
    const intervals = INTERVAL_ORDER.filter((iv) => counts[iv]);
    const dueLimit = addDaysIso(DUE_SOON_DAYS);
    return {
      count: rows.length,
      intervals,
      countByInterval: intervals.map((iv) => counts[iv]),
      active: rows.filter((r) => r.isActive).length,
      monthlyVolume: sumBy(
        rows,
        (r) => Number(r.grossPreview) * (MONTHLY_FACTOR[r.interval] || 0),
        (r) => r.isActive,
      ),
      dueSoon: rows.filter((r) => r.isActive && r.nextRunDate && r.nextRunDate <= dueLimit).length,
    };
  }, [data]);

  const generate = useMutation({
    mutationFn: (id) => financeApi.generateRecurring(id),
    onSuccess: (res) => {
      const numbers = (res?.invoices || []).map((inv) => inv.invoiceNumber).filter(Boolean);
      showToast(`${t('recurring.generated')} ${numbers.join(', ')}`, 'success');
      queryClient.invalidateQueries({ queryKey: ['finance', 'recurring'] });
      queryClient.invalidateQueries({ queryKey: ['finance', 'invoices'] });
    },
    onError: (err) => showToast(err?.message || t('common.loadError'), 'danger'),
  });

  const setActive = useMutation({
    mutationFn: ({ id, isActive }) => financeApi.setRecurringActive(id, isActive),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['finance', 'recurring'] }),
    onError: (err) => showToast(err?.message || t('common.loadError'), 'danger'),
  });

  const remove = useMutation({
    mutationFn: (id) => financeApi.deleteRecurring(id),
    onSuccess: () => {
      setDeleteTarget(null);
      queryClient.invalidateQueries({ queryKey: ['finance', 'recurring'] });
    },
    onError: (err) => {
      setDeleteTarget(null);
      showToast(err?.message || t('common.loadError'), 'danger');
    },
  });

  async function openEdit(row) {
    try {
      const detail = await financeApi.getRecurring(row.id).then((r) => r.items);
      setModalState({ template: detail, customerLabel: customerNames[detail.customerId] || '' });
    } catch (err) {
      showToast(err?.message || t('common.loadError'), 'danger');
    }
  }

  function toggleRow(row) {
    setExpandedId((current) => (current === row.id ? null : row.id));
  }

  const columns = [
    t('recurring.col.name'),
    t('recurring.col.customer'),
    t('recurring.col.interval'),
    t('recurring.col.nextRun'),
    t('recurring.col.amount'),
    t('col.status'),
    t('common.actions'),
  ];

  const rows = data || [];

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('recurring.title')}</h1>
          <p className="muted">{t('recurring.subtitle')}</p>
        </div>
        <div className="page-actions">
          <Button variant="primary" onClick={() => setModalState({ template: null, customerLabel: '' })}>
            {t('recurring.action.new')}
          </Button>
        </div>
      </header>

      {stats.count > 0 && (
        <InsightBar
          title={t('recurring.insight.byInterval')}
          labels={stats.intervals.map((iv) => t(`recurring.interval.${iv}`))}
          values={stats.countByInterval}
          colors={stats.intervals.map((iv) => palette?.series?.[INTERVAL_ORDER.indexOf(iv)])}
          metrics={[
            { label: t('recurring.insight.active'), value: stats.active },
            { label: t('recurring.insight.monthly'), value: formatCurrency(stats.monthlyVolume) },
            { label: t('recurring.insight.due30'), value: stats.dueSoon },
            { label: t('insight.count'), value: stats.count },
          ]}
        />
      )}

      {error && <div className="alert danger">{error.message}</div>}

      {isLoading ? (
        <div className="table-wrap">
          <Skeleton variant="table" />
        </div>
      ) : rows.length === 0 ? (
        <div className="table-wrap">
          <EmptyState message={t('recurring.empty')} />
        </div>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                {columns.map((label, idx) => (
                  <th key={idx}>{label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const isExpanded = expandedId === row.id;
                const detailRowId = `recurring-runs-${row.id}`;
                const isOverdue = row.isActive && row.nextRunDate && row.nextRunDate < today;
                return (
                  <Fragment key={row.id}>
                    <tr className="audit-row">
                      <td>
                        <button
                          type="button"
                          className="audit-toggle"
                          aria-expanded={isExpanded}
                          aria-controls={detailRowId}
                          aria-label={t('recurring.toggleRuns')}
                          onClick={() => toggleRow(row)}
                        >
                          {isExpanded ? '▾' : '▸'}
                        </button>{' '}
                        {row.name}
                      </td>
                      <td>{customerNames[row.customerId] || row.customerId.slice(0, ID_SHORT_LENGTH)}</td>
                      <td>
                        <Badge variant={INTERVAL_BADGE_VARIANTS[row.interval] || 'neutral'}>
                          {t(`recurring.interval.${row.interval}`)}
                        </Badge>
                      </td>
                      <td style={isOverdue ? { color: 'var(--color-danger)', fontWeight: 600 } : undefined}>
                        {formatDate(row.nextRunDate)}
                      </td>
                      <td className="num mono">{formatCurrency(row.grossPreview)}</td>
                      <td>
                        <Badge variant={row.isActive ? 'success' : 'neutral'}>
                          {row.isActive ? t('recurring.active') : t('recurring.paused')}
                        </Badge>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          {row.isActive && (
                            <Button
                              size="sm"
                              variant="primary"
                              disabled={generate.isPending}
                              onClick={() => generate.mutate(row.id)}
                            >
                              {t('recurring.action.generate')}
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="default"
                            disabled={setActive.isPending}
                            onClick={() => setActive.mutate({ id: row.id, isActive: !row.isActive })}
                          >
                            {row.isActive ? t('recurring.action.pause') : t('recurring.action.resume')}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => openEdit(row)}>
                            {t('recurring.action.edit')}
                          </Button>
                          <Button size="sm" variant="danger" onClick={() => setDeleteTarget(row)}>
                            {t('recurring.action.delete')}
                          </Button>
                        </div>
                      </td>
                    </tr>
                    {isExpanded && (
                      <RecurringRunsRow row={row} colSpan={columns.length} id={detailRowId} />
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {modalState && (
        <RecurringInvoiceModal
          key={modalState.template?.id ?? 'new'}
          open
          template={modalState.template}
          customerLabel={modalState.customerLabel}
          onClose={() => setModalState(null)}
        />
      )}

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title={t('recurring.action.delete')}
        message={t('recurring.confirmDelete')}
        confirmLabel={t('recurring.action.delete')}
        variant="danger"
        isPending={remove.isPending}
        onConfirm={() => remove.mutate(deleteTarget.id)}
        onClose={() => setDeleteTarget(null)}
      />
    </section>
  );
}
